/* eslint-disable max-len */
import React, { useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import styled from 'styled-components';
import { VALID_STATUS_PROCESS_ONBOARDING_EMPLOYEES } from 'config/const';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { generateEmployeeListRequest, processOnboardingEmployeesRequest, Site } from 'store/Site';
import { IStore } from 'store/types';
import { IApiErrorPayloadWithCode, IOnboardingEmployeeList, IProcessOnboardingEmployeesResponse } from 'types';
import { formatDateForApi, sortAlphaNumerically } from 'utils';
import { AlertHeader, Button, HR, LoaderSpin, Message, ModalFactory, ModalTitle as ModalTitleBase, Switch, Text } from '..';
import { Appearance, lineHeight, theme, toRem } from '../../design';
import { ModalContent } from '../../screens/SiteSettings/TipCalculator/shared/styles';
import { OnboardingEmployee } from './OnboardingEmployee';
import { OnboardingSummaryListItem } from './OnboardingSummaryListItem';

const Content = styled(ModalContent)`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${toRem(theme.space.lg)};
  width: 100%;
`;

const StyledSpinner = styled(LoaderSpin)`
  margin: ${toRem(theme.space.lg)} 0;
`;

export const LoadingText = styled(Text)`
  margin-top: ${toRem(theme.space.sm)};
  margin-bottom: ${toRem(theme.space.xl)};
  text-align: center;
`;

const ModalSubtitle = styled(Text)`
  text-align: center;
  margin-top: ${toRem(theme.space.lg)};
`;

const ModalTitle = styled(ModalTitleBase)`
  font-size: ${toRem(theme.font.size['2xl'])};
  line-height: ${lineHeight(theme.font.size['2xl'])};
  margin-bottom: ${toRem(theme.space.xs)};
`;

const HeaderContainer = styled.div`
  display: block;
  width: 100%;
`;

const SwitchContainer = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  justify-content: center;
  gap: ${toRem(theme.space.sm)};
  margin-top: ${toRem(theme.space.lg)};
`;

const EmployeeListWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${toRem(theme.space.lg)};
  width: 100%;
`;

const InfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: ${toRem(theme.space.xs)};
  width: 100%;
  margin-bottom: ${toRem(theme.space.lg)};
`;

const InfoItems = styled.ul`
  list-style: disc;
  padding-left: ${toRem(theme.space.lg)};
  display: flex;
  flex-direction: column;
  gap: ${toRem(theme.space.xs)};
  margin: ${toRem(theme.space.sm)} 0;
`;

const InfoItem = styled.li`
  font-size: ${toRem(theme.font.size.sm)};
`;

const SummaryContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${toRem(theme.space.sm)};
  width: 100%;
`;

type ModalType = 'generate' | 'result' | 'summary' | 'error';
const DEFAULT_START_DATE = formatDateForApi(dayjs().subtract(7, 'day'));
const DEFAULT_END_DATE = formatDateForApi(dayjs().subtract(1, 'day'));

interface IEmployeeOnboardingModalProps {
  siteId: string;
  onClose?(): void;
  dataCy?: string;
}

export const EmployeeOnboardingModal: React.FC<IEmployeeOnboardingModalProps> = ({ siteId, onClose, dataCy = 'employee-onboarding-modal' }) => {
  const dispatch = useDispatch();
  const [modal, setModal] = useState<ModalType>('generate');
  const [error, setError] = useState<IApiErrorPayloadWithCode | null>(null);
  const isLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Site.GENERATE_EMPLOYEE_LIST_REQUEST));
  const isProcessOnboardingEmployeesLoading = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Site.PROCESS_ONBOARDING_EMPLOYEES_REQUEST));
  const [employeesList, setEmployeesList] = useState<IOnboardingEmployeeList>();
  const [summary, setSummary] = useState<IProcessOnboardingEmployeesResponse>();
  const [showIneligibleEmployees, setShowIneligibleEmployees] = useState(false);

  const handleGenerateEmployeesList = () => {
    const handleSuccess = (employees: IOnboardingEmployeeList) => {
      setEmployeesList(employees);
      setModal('result');
    };

    dispatch(generateEmployeeListRequest(siteId, DEFAULT_START_DATE, DEFAULT_END_DATE, {}, handleSuccess, handleError));
  };

  const handleError = (error: any) => {
    setError(error);
    setModal('error');
  };

  const onAddEmployees = (onboardingEmployeesList: IOnboardingEmployeeList) => {
    const handleProcessOnboardingEmployeesSuccess = (summary: IProcessOnboardingEmployeesResponse) => {
      setSummary(summary);
      setModal('summary');
    };

    dispatch(processOnboardingEmployeesRequest(siteId, onboardingEmployeesList, handleProcessOnboardingEmployeesSuccess, handleError));
  };

  // Filter out ineligible employees
  const eligibleEmployees = useMemo(() => (employeesList?.employees ?? []).filter((employee) => VALID_STATUS_PROCESS_ONBOARDING_EMPLOYEES.includes(employee.onboard.status)), [employeesList]);

  // Show all employees if showIneligibleEmployees is true, otherwise show only eligible employees
  const filteredEmployeesList = showIneligibleEmployees ? employeesList?.employees : eligibleEmployees;

  // Check if there are any eligible employees
  const hasEligibleEmployees = eligibleEmployees.length > 0;

  return (
    <ModalFactory dataCy={dataCy} onClose={onClose} isActive maxWidth={640} closeOnOutSideClick={false}>
      {modal === 'generate' && !isLoading && (
        <Content data-cy={`${dataCy}-generate-content`}>
          <HeaderContainer>
            <ModalTitle>Employee Onboarding</ModalTitle>
            <ModalSubtitle size="small">Generate a list of employees from the POS system.</ModalSubtitle>
          </HeaderContainer>
          <InfoWrapper>
            <Text size="small" fontWeight="semibold">
              How this process works:
            </Text>
            <Text size="small">Each employee is evaluated against existing records using employee ID, email, and name. Based on this match, we determine the correct action: </Text>
            <InfoItems>
              <InfoItem>Create new employees when no match is found</InfoItem>
              <InfoItem>Link employees to the integration when a match exists</InfoItem>
              <InfoItem>Reactivate previously dismissed employees</InfoItem>
              <InfoItem>Skip records that are already onboarded, incomplete, or ambiguous</InfoItem>
            </InfoItems>
            <Text size="small">We intentionally skip any records with conflicts, missing data, or restricted roles to ensure data integrity and prevent unintended changes.</Text>
          </InfoWrapper>
          <Button type="submit" appearance="primary" isProcessing={isLoading} onClick={handleGenerateEmployeesList} label="Generate Employees List" data-cy={`${dataCy}-generate-employees-list-button`} />
        </Content>
      )}

      {modal === 'generate' && isLoading && (
        <>
          <ModalTitle>Generating Employees List</ModalTitle>
          <StyledSpinner color={theme.color.secondary[400]} size={64} stroke={6} />
          <Text size="small">Please wait while we generate the report.</Text>
        </>
      )}

      {modal === 'result' && (
        <Content data-cy={`${dataCy}-result-content`}>
          <HeaderContainer>
            <ModalTitle>Employee Onboarding</ModalTitle>
            <ModalSubtitle size="small">The following is a list of employees generated from the POS system.</ModalSubtitle>
            <SwitchContainer>
              <Switch data-cy="filter-toggle" checked={showIneligibleEmployees} onChange={() => setShowIneligibleEmployees(!showIneligibleEmployees)} />
              <Text size="small">Show Ineligible Employees</Text>
            </SwitchContainer>
          </HeaderContainer>
          <HR />
          {filteredEmployeesList?.length > 0 ? (
            <>
              <EmployeeListWrapper>
                {[...filteredEmployeesList].sort(sortAlphaNumerically('firstName', 'lastName')).map((employee) => (
                  <OnboardingEmployee key={employee.providerId} employee={employee} />
                ))}
              </EmployeeListWrapper>
              <Button
                type="button"
                appearance="primary"
                onClick={() => employeesList && onAddEmployees(employeesList)}
                isProcessing={isProcessOnboardingEmployeesLoading}
                label="Add Employees to Site"
                data-cy={`${dataCy}-add-employees-to-site-button`}
                disabled={!hasEligibleEmployees}
              />
            </>
          ) : (
            <Message appearance={Appearance.WARNING} title="No employees to add" message="Please check the employees list and try again." />
          )}
        </Content>
      )}

      {modal === 'summary' && (
        <Content data-cy={`${dataCy}-summary-content`}>
          <HeaderContainer>
            <ModalTitle>Employee Onboarding Summary</ModalTitle>
            <ModalSubtitle size="small">The following is a summary of the employees onboarded.</ModalSubtitle>
          </HeaderContainer>
          <SummaryContainer>
            {summary?.toCreate?.length > 0 && (
              <OnboardingSummaryListItem title="New Employees Created" description={`A total of ${summary.toCreate.length} new employees were created.`} employees={summary.toCreate} dataCy="new-employees-created" />
            )}
            {summary?.addSiteRole?.length > 0 && (
              <OnboardingSummaryListItem title="Employees Added to Site" description={`A total of ${summary.addSiteRole.length} employees were added to the site.`} employees={summary.addSiteRole} dataCy="employees-added-to-site" />
            )}
            {summary?.toLink?.length > 0 && <OnboardingSummaryListItem title="Employees Linked" description={`A total of ${summary.toLink.length} employees were linked.`} employees={summary.toLink} dataCy="employees-linked" />}
            {(summary?.toReactivate?.length > 0 || summary?.toReactivateSite?.length > 0) && (
              <OnboardingSummaryListItem
                title="Employees Reactivated"
                description={`A total of ${(summary?.toReactivate?.length || 0) + (summary?.toReactivateSite?.length || 0)} employees were reactivated.`}
                employees={[...(summary?.toReactivate ?? []), ...(summary?.toReactivateSite ?? [])]}
                dataCy="employees-reactivated"
              />
            )}
            {showIneligibleEmployees && summary?.toSkip?.length > 0 && (
              <OnboardingSummaryListItem title="Employees Skipped" description={`A total of ${summary.toSkip.length} employees were skipped.`} employees={summary.toSkip} dataCy="employees-skipped" />
            )}
            {showIneligibleEmployees && summary?.ineligible?.length > 0 && (
              <OnboardingSummaryListItem title="Employees Ineligible" description={`A total of ${summary.ineligible.length} employees were ineligible.`} employees={summary.ineligible} dataCy="employees-ineligible" />
            )}
          </SummaryContainer>
        </Content>
      )}

      {modal === 'error' && (
        <Content data-cy={`${dataCy}-error-content`}>
          <HeaderContainer>
            <AlertHeader size={64} />
            <ModalTitle>We are having trouble</ModalTitle>
            <ModalSubtitle size="small">An error occurred while generating the employees list.</ModalSubtitle>
          </HeaderContainer>
          <Message className="message" appearance={Appearance.WARNING} message={error.errorMessages?.[0] || 'An error occurred while generating the employees list.'} />
        </Content>
      )}
    </ModalFactory>
  );
};
